package com.downdogapp.client.singleton

import com.downdogapp.Size.Companion.px
import kotlinx.browser.document
import kotlinx.browser.window

object Browser {

  val windowWidth get() = px(window.innerWidth)
  val windowHeight get() = px(window.innerHeight)

  private val browser: String
  private val browserVersion: String

  init {
    //  From http://jsfiddle.net/ChristianL/AVyND/.  Detecting OS info by browser
    //  Works for Chrome, Safari, Firefox on Mac (Jan 19 2018)
    val agent = window.navigator.userAgent
    // TODO: Use this code to be more functional & kotlin like
    agent.apply {
      when {
        contains("Opera") -> {
          browser = "Opera"
          browserVersion =
            if (contains("Version"))
              substring(this.indexOf("Version") + 8)
            else
              substring(this.indexOf("Opera") + 6)
        }

        contains("OPR") -> {
          browser = "Opera"
          browserVersion = substring(this.indexOf("OPR") + 4)
        }

        contains("Edge") -> {
          browser = "Microsoft Edge"
          browserVersion = substring(this.indexOf("Edge") + 5)
        }

        contains("MSIE") -> {
          browser = "Microsoft Internet Explorer"
          browserVersion = substring(this.indexOf("MSIE") + 5)
        }

        contains("Chrome") -> {
          browser = "Chrome"
          browserVersion = substring(this.indexOf("Chrome") + 7)
        }

        contains("Safari") -> {
          browser = "Safari"
          browserVersion =
            if (contains("Version"))
              substring(this.indexOf("Version") + 8)
            else
              substring(this.indexOf("Safari") + 7)
        }

        contains("Firefox") -> {
          browser = "Firefox"
          browserVersion = substring(this.indexOf("Firefox") + 8)
        }

        else -> {
          browser = "Other"
          browserVersion = ""
        }
      }
    }
  }

  val deviceDescription: String
    get() {
      val trimmedBrowserVersion =
        browserVersion
          .substringBefore(';')
          .substringBefore(' ')
          .substringBefore(')')

      return "$browser $trimmedBrowserVersion"
    }

  val osVersion: String
    get() {
      val agent = window.navigator.userAgent

      // Include version number for Mac OS X
      // TODO Figure out equivalent for windows, if one exists.
      val osXVersionRegex = """Mac OS X [._\d]+""".toRegex()
      if (osXVersionRegex.containsMatchIn(agent)) {
        return agent.substring(osXVersionRegex.find(agent)!!.range)
      }

      val osNamesAndPatterns = listOf(
        "Windows 10" to "Windows 10.0|Windows NT 10.0",
        "Windows 8.1" to "Windows 8.1|Windows NT 6.3",
        "Windows 8" to "Windows 8|Windows NT 6.2",
        "Windows 7" to "Windows 7|Windows NT 6.1",
        "Windows Vista" to "Windows NT 6.0",
        "Windows Server 2003" to "Windows NT 5.2",
        "Windows XP" to "Windows NT 5.1|Windows XP",
        "Windows 2000" to "Windows NT 5.0|Windows 2000",
        "Windows ME" to "Win 9x 4.90|Windows ME",
        "Windows 98" to "Windows 98|Win98",
        "Windows 95" to "Windows 95|Win95|Windows_95",
        "Windows NT 4.0" to "Windows NT 4.0|WinNT4.0|WinNT|Windows NT",
        "Windows CE" to "Windows CE",
        "Windows 3.11" to "Win16",
        "Android" to "Android",
        "Open BSD" to "OpenBSD",
        "Sun OS" to "SunOS",
        "Linux" to "Linux|X11",
        "iOS" to "iPhone|iPad|iPod",
        "Mac OS X" to "Mac OS X",
        "Mac OS" to "MacPPC|MacIntel|Mac_PowerPC|Macintosh",
        "QNX" to "QNX",
        "UNIX" to "UNIX",
        "BeOS" to "BeOS",
        "OS/2" to "OS\\/2",
        "Search Bot" to "nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\\/Teoma|ia_archiver"
      )

      for ((osName, pattern) in osNamesAndPatterns) {
        if (pattern.toRegex().containsMatchIn(agent)) {
          return osName
        }
      }

      return ""
    }

  val isPhone = osVersion in setOf("iOS", "Android")

  val isAndroid = osVersion in setOf("Android")
  val isIos = osVersion in setOf("iOS")

  val isFirefox = browser == "Firefox"

  val isChrome = browser == "Chrome"

  val isSafari = browser == "Safari"

  private fun firstValidField(vararg fields: dynamic) =
    fields.firstOrNull {
      it != undefined
    }

  // For all full screen functionality, see: See https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API#Prefixing

  // Whether or not full screen mode is available
  val fullscreenEnabled: Boolean
    get() {
      val d = document.asDynamic()

      return firstValidField(
        d.webkitFullscreenEnabled,  // Chrome, Opera, Edge, Safari
        d.mozFullScreenEnabled,     // Firefox
        d.msFullscreenEnabled       // IE 11
      ) as? Boolean
             ?: false
    }

  // Whether or not document is currently in full screen mode
  val fullscreen: Boolean
    get() {
      if (!fullscreenEnabled) return false

      val d = document.asDynamic()

      return firstValidField(
        d.webkitIsFullScreen, // Chrome, Opera, Edge, Safari
        d.mozFullScreen       // Firefox
      ) as Boolean?
             ?: (
               if (d.msFullscreenElemenent == null)
                 null
               else d.msFullscreenElement.toString() != "null"
                ) // Null case checks IE 11
             ?: false
    }

  fun onFullScreenChange(block: () -> Unit) {
    if (fullscreenEnabled) {
      val d = document.asDynamic()

      d.onwebkitfullscreenchange = { block() }    // Chrome, Opera, Edge, Safari
      d.onmozfullscreenchange = { block() }       // Firefox
      d.MSFullscreenChange = { block() }          // IE 11
    }
  }

  fun requestFullscreen() {
    if (fullscreenEnabled && !fullscreen) {
      val b = document.body.asDynamic()

      val requestFS =
        firstValidField(
          b.webkitRequestFullscreen,  // Chrome, Opera, Edge, Safari
          b.mozRequestFullScreen,     // Firefox
          b.msRequestFullscreen       // IE 11
        )

      requestFS.call(document.body)
    }
  }

  fun exitFullscreen() {
    if (fullscreenEnabled && fullscreen) {
      val d = document.asDynamic()

      val exitFS =
        firstValidField(
          d.webkitExitFullscreen,   // Chrome, Opera, Edge, Safari
          d.mozCancelFullScreen,    // Firefox
          d.msExitFullscreen        // IE 11
        )

      exitFS.call(document)
    }
  }
}
